import {Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography} from "@mui/material";
import React from "react";
import {useLocalization} from "../../../i18n";
import {Instant, TemporalRangeFormat, useFlag} from "@variocube/app-ui";
import {useBooking} from "../../../context/booking";
import {DisplayCurrency, notBlank} from "../../../utils";
import {Accessibility} from "../../../component/accessibility";
import {BoxedInfo} from "../../../component/boxed-info";

export function BookingInfoButton() {
	const {t, language} = useLocalization();
	const {booking: {
		site,
		name,
		email,
		phone,
		from,
		until,
		timezone,
		occupancies,
		paymentId,
		payments,
		paymentStatus
	} = {}} = useBooking();

	const [open, setOpen, clearOpen] = useFlag(false);

	const detailsRows = [
		{
			label: t('booking.site'),
			value: (
				<>
					<Typography><strong>{site?.name}</strong></Typography>
					<Typography variant="body2">{site?.street}</Typography>
					<Typography variant="body2">{site?.city} {site?.country}</Typography>
					{notBlank(site?.accessDescription) && (
						<Alert severity="info" sx={{ mt: 2 }}>{site?.accessDescription}</Alert>
					)}
				</>
			),
		},
		{
			label: t('sites.accessibility'),
			value: site !== undefined && <Accessibility site={site} />
		},
		...([
				{
					label: t('customer.email'),
					value: email
				},
				{
					label: t('customer.name'),
					value: name
				},
				{
					label: t('customer.phone'),
					value: phone
				},
			]
				.filter(({value}) => notBlank(value))
		),
		{
			label: t('dates.period'),
			value: notBlank(from) && notBlank(until) && (
				<Typography variant="body2">
					<TemporalRangeFormat
						locale={language}
						from={Instant.from(from as string)}
						until={Instant.from(until as string)}
					/>
					&nbsp;
					{timezone}
				</Typography>
			)
		},
		{
			label: t('boxes.title'),
			value: (occupancies ?? []).map(l => l.boxNumber).join(", ")
		},
		...(!paymentId ? [] : [{
			label: t('payment.status'),
			value: `${paymentStatus}`.toUpperCase(),
		}]),
		...((payments ?? []).length == 0 ? [] : [{
			label: t('payment.title'),
			value: (payments ?? []).map(p => (<Typography>{site?.currency ?? 'EUR'} <DisplayCurrency amount={p.amount} /></Typography>))
		}])
	]

	return (
		<>
			<Button
				variant="contained"
				color="primary"
				disableElevation
				onClick={setOpen}
			>
				{t('booking.simplified.cta')}
			</Button>

			<Dialog
				open={open}
				fullWidth
				maxWidth="sm"
				onClose={clearOpen}
			>
				<DialogTitle>{t('booking.simplified.cta')}</DialogTitle>
				<DialogContent>
					{detailsRows.map(({label, value}, i) => (
						<BoxedInfo
							key={'info-' + i}
							label={label.toUpperCase()}
							labelAlignment="left"
							contentDirection="column"
							contentAlignment="flex-start"
							content={value as any}
						/>
					))}
				</DialogContent>
				<DialogActions>
					<Button
						fullWidth
						size="small"
						variant="outlined"
						color="primary"
						disableElevation
						onClick={clearOpen}
					>
						{t('actions.close')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	)
}
